import React, { useState, useEffect, useMemo } from 'react';
import {
  FiPlus,
  FiSearch,
  FiEye,
  FiTrash2,
  FiCheckCircle,
  FiFilter,
  FiCalendar,
  FiEdit,
  FiLoader,
  FiShare2,
  FiCopy,
  FiExternalLink
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../../Api';
import { usePermission } from '../../../hooks/usePermission';
import {
  PageComponent,
  Card,
  Table,
  Button,
  Input,
  Modal,
  Skeletons,
  DatePeriodFilter,
  getDateParams,
  TableActions,
  Select,
  MultiSelect,
} from '../../../components';
import SelectInspectionModal from './SelectInspectionModal';
import ExecutionModal from './ExecutionModal';
import ViewExecutionModal from './ViewExecutionModal';
import '../../CompanyManager/style.css';
import './style.css';

export default function InspectionExecution() {
  const permissions = usePermission('inspections');
  const [companies, setCompanies] = useState([]);
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  // const [executorFilter, setExecutorFilter] = useState('all'); // Removed executor dropdown filter

  const [executions, setExecutions] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  
  // Date Filter State
  const [periodMode, setPeriodMode] = useState('daily');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [customPeriod, setCustomPeriod] = useState({ startDate: '', endDate: '' });

  const datePeriod = useMemo(() => {
    return getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);
  }, [periodMode, currentDate, customPeriod]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, datePeriod, companyFilterValues]);

  // Modal State
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const [isExecutionModalOpen, setIsExecutionModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplateCompanyId, setSelectedTemplateCompanyId] = useState(null);
  const [executionToEdit, setExecutionToEdit] = useState(null);

  // View/Delete Modal State
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [executionToView, setExecutionToView] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [executionToDelete, setExecutionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Share Modal State
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [isGeneratingShare, setIsGeneratingShare] = useState(false);

  const [loadingAction, setLoadingAction] = useState({ id: null, type: null });

  useEffect(() => {
    (async () => {
      try {
        const data = await Api.getCompanies();
        const list = Array.isArray(data) ? data : (data.data || []);
        setCompanies(list);
        setCompanyFilterValues(['all']);
      } catch (error) {
        toast.error('Erro ao carregar empresas.');
      }
    })();
  }, []);

  useEffect(() => {
    if (companies.length === 0 || !permissions.canView) return;
    loadExecutions();
  }, [datePeriod, companyFilterValues, companies, permissions.canView, currentPage, pageSize, debouncedSearchTerm]);

  // Removed client-side uniqueExecutors calculation as it requires all data

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      if (specificIds.length === companies.length && companies.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);
  }

  async function loadExecutions() {
    if (companies.length === 0) return;

    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: pageSize,
      };
      
      if (debouncedSearchTerm) {
        params.searchTerm = debouncedSearchTerm;
      }

      if (datePeriod.startDate) {
        params.startDate = datePeriod.startDate;
      }
      if (datePeriod.endDate) {
        params.endDate = datePeriod.endDate;
      }

      const allValue = 'all';
      const specificIds = companyFilterValues.filter(v => v !== allValue);
      const hasAllSelected = companyFilterValues.includes(allValue);

      const ids = hasAllSelected || specificIds.length === 0
        ? companies.map(c => c.id)
        : specificIds.map(id => Number(id));

      if (ids.length === 0) {
        setExecutions([]);
        setTotalItems(0);
        return;
      }

      // Note: If multiple companies are selected, we might have an issue with pagination
      // because the backend paginates PER company.
      // If the user selects "All Companies", and we call the API for each company,
      // we get Page 1 for Company A, Page 1 for Company B, etc.
      // Merging them client-side breaks the "Page 1 of Global List" concept.
      // However, usually the user filters by ONE company or the backend supports "all" context.
      // For now, if multiple companies are selected, we will fetch Page 1 for ALL of them and merge,
      // effectively showing more items than pageSize. This is a trade-off.
      // A better solution would be a global endpoint /inspection-executions that accepts companyIds.
      // But given the constraints, we proceed with the existing pattern.
      
      if (hasAllSelected || specificIds.length !== 1) {
        // Warning: This is not efficient for "All Companies" pagination, but matches previous behavior logic
        // We will fetch page 1 from all companies? That's too much data.
        // We should probably limit this. 
        // Ideally, we pick the first company or require a single company filter for pagination.
        // Or we just fetch and merge (which might be slow if many companies).
        
        const responses = await Promise.all(ids.map(id => Api.getExecutedInspections(id, params)));

        let mergedData = [];
        let total = 0;

        responses.forEach(r => {
            if (r.data && Array.isArray(r.data)) {
                mergedData = [...mergedData, ...r.data];
                total += r.total || 0;
            } else if (Array.isArray(r)) {
                mergedData = [...mergedData, ...r];
                total += r.length;
            }
        });

        // We sort the merged page results
        mergedData.sort((a, b) => new Date(b.executed_at) - new Date(a.executed_at));
        
        setExecutions(mergedData);
        setTotalItems(total); // This total is rough sum of totals
      } else {
        const singleId = ids[0];
        const response = await Api.getExecutedInspections(singleId, params);

        if (response.data && Array.isArray(response.data)) {
          setExecutions(response.data);
          setTotalItems(response.total || 0);
        } else if (Array.isArray(response)) {
           // Fallback for legacy response
          setExecutions(response);
          setTotalItems(response.length);
        } else {
          setExecutions([]);
          setTotalItems(0);
        }
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar execuções.');
    } finally {
      setLoading(false);
    }
  }

  function handleNewExecution() {
    setIsSelectModalOpen(true);
  }

  function handleTemplateSelected(template, companyId) {
    setSelectedTemplate(template);
    setSelectedTemplateCompanyId(companyId);
    setIsSelectModalOpen(false);
    setIsExecutionModalOpen(true);
  }

  function handleExecutionSuccess() {
    loadExecutions();
  }

  async function handleEdit(execution) {
    const companyId = execution.company_id;
    if (!companyId) return;
    setLoadingAction({ id: execution.id, type: 'edit' });
    try {
      const fullExecution = await Api.getExecutedInspectionById(companyId, execution.id);
      
      if (!fullExecution.config) {
        toast.error('Configuração da inspeção não encontrada.');
        return;
      }

      setExecutionToEdit(fullExecution);
      setSelectedTemplate(fullExecution.config);
      setSelectedTemplateCompanyId(companyId);
      setIsExecutionModalOpen(true);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar dados para edição.');
    } finally {
      setLoadingAction({ id: null, type: null });
    }
  }

  async function handleView(execution) {
    const companyId = execution.company_id;
    if (!companyId) return;
    setLoadingAction({ id: execution.id, type: 'view' });
    try {
      const fullExecution = await Api.getExecutedInspectionById(companyId, execution.id);
      setExecutionToView(fullExecution);
      setIsViewModalOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes da execução.');
    } finally {
      setLoadingAction({ id: null, type: null });
    }
  }

  function handleDelete(execution) {
    setExecutionToDelete(execution);
    setIsDeleteModalOpen(true);
  }

  async function confirmDelete() {
    if (!executionToDelete) return;
    
    setIsDeleting(true);
    try {
      const companyId = executionToDelete.company_id;
      await Api.deleteExecutedInspection(companyId, executionToDelete.id);
      setExecutions(prev => prev.filter(i => i.id !== executionToDelete.id));
      toast.success('Execução excluída com sucesso!');
      setIsDeleteModalOpen(false);
      setExecutionToDelete(null);
    } catch (error) {
      toast.error('Erro ao excluir execução.');
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleShare(execution) {
    setIsGeneratingShare(true);
    try {
      const response = await Api.shareInspection(execution.id);
      const token = response.public_token;
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/inspection/public/${token}`;
      setShareLink(link);
      setIsShareModalOpen(true);
    } catch (error) {
      toast.error('Erro ao gerar link de compartilhamento.');
    } finally {
      setIsGeneratingShare(false);
    }
  }

  function copyShareLink() {
    navigator.clipboard.writeText(shareLink);
    toast.success('Link copiado para a área de transferência!');
  }

  async function handleNativeShare() {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Relatório de Inspeção - Grupo Maxximus',
          text: 'Confira o relatório da inspeção realizada:',
          url: shareLink,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          toast.error('Erro ao compartilhar.');
        }
      }
    } else {
      toast.info('Compartilhamento não suportado neste navegador. Use o botão de copiar link.');
    }
  }

  // Removed client-side filteredExecutions logic

  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;

  const columns = [
    {
      id: 'name',
      header: 'Inspeção',
      width: '2fr',
      cellClassName: 'manager-table-cell-primary',
      render: (item) => (
        <>
          <div className="manager-table-avatar" style={{ color: item.config?.color || item.color || '#10B981' }}>
            <FiCheckCircle size={18} />
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">{item.inspection_name}</span>
            <span className="manager-table-subtitle">Exec: #{item.id} {item.inspection_config_id ? `| Mod: #${item.inspection_config_id}` : ''}</span>
          </div>
        </>
      )
    },
    {
      id: 'reference',
      header: 'Referência',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: (item) => {
        // If the backend sends references processed, display them nicely
        // If it's a simple string, display it
        // If we want to highlight machinery/vehicle specifically
        // item.reference is usually a string from the backend (vehicle label, station label, or comma-separated)
        
        // Let's try to parse if it looks like a list or check if we can enhance it
        return <span className="manager-table-subtitle">{item.reference || '-'}</span>
      }
    },
    {
      id: 'company',
      header: 'Empresa',
      width: '1fr',
      cellClassName: 'manager-table-cell',
      render: (item) => {
        const companyObj = companies.find(c => String(c.id) === String(item.company_id));
        const name = companyObj ? (companyObj.corporate_name || companyObj.trade_name || companyObj.name || `Empresa ${companyObj.id}`) : '-';
        return <span className="manager-table-subtitle">{name}</span>;
      }
    },
    {
      id: 'executor',
      header: 'Executor',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: (item) => (
        <span className="manager-table-subtitle">{item.executor_name || 'N/A'}</span>
      )
    },
    {
      id: 'date',
      header: 'Data Execução',
      width: '1fr',
      cellClassName: 'manager-table-cell',
      render: (item) => (
        <span className="manager-table-subtitle">
          {item.executed_at ? new Date(item.executed_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
        </span>
      )
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: (item) => (
        <TableActions>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleView(item);
            }}
            disabled={loadingAction.id === item.id}
          >
            {loadingAction.id === item.id && loadingAction.type === 'view' ? (
              <FiLoader size={16} className="icon-spin" />
            ) : (
              <FiEye size={16} />
            )}
            Visualizar
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleShare(item);
            }}
            disabled={isGeneratingShare}
          >
            <FiShare2 size={16} />
            Compartilhar
          </button>
          {permissions.canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(item);
              }}
              disabled={loadingAction.id === item.id}
            >
              {loadingAction.id === item.id && loadingAction.type === 'edit' ? (
                <FiLoader size={16} className="icon-spin" />
              ) : (
                <FiEdit size={16} />
              )}
              Editar
            </button>
          )}
          {permissions.canDelete && (
            <button
              type="button"
              className="action-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(item);
              }}
            >
              <FiTrash2 size={16} />
              Excluir
            </button>
          )}
        </TableActions>
      )
    }
  ];

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiCheckCircle size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar este módulo.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        <div className="manager-header">
          <div className="manager-header-title">
            <div className="manager-header-icon">
              <FiCheckCircle size={22} />
            </div>
            <div>
              <h1>Inspeções Executadas</h1>
              <p>Gerencie as inspeções realizadas</p>
            </div>
          </div>
          {permissions.canCreate && (
            <Button 
              onClick={handleNewExecution} 
              className="manager-header-button"
              startIcon={<FiPlus size={18} />}
            >
              Nova Execução
            </Button>
          )}
        </div>

        <Card className="manager-filters-card">
          <div className="manager-filters-header">
            <div className="manager-filters-title">
              <FiFilter size={16} />
              <span>Filtros</span>
            </div>
          </div>

          <div className="manager-filters-grid-custom">
            <div className="manager-filter-group">
              <DatePeriodFilter
                periodMode={periodMode}
                setPeriodMode={setPeriodMode}
                currentDate={currentDate}
                setCurrentDate={setCurrentDate}
                customRange={customPeriod}
                setCustomRange={setCustomPeriod}
              />
            </div>
            <div className="manager-filter-group">
              <label className="manager-filter-label">Empresa</label>
              <MultiSelect
                id="company-filter"
                options={[
                  { value: 'all', label: 'TODAS' },
                  ...companies.map(c => ({
                    value: String(c.id),
                    label: c.corporate_name || c.trade_name || c.name || `Empresa ${c.id}`
                  }))
                ]}
                value={companyFilterValues}
                onChange={handleCompanyFilterChange}
                searchable
              />
            </div>

            <div className="manager-filter-group">
              <label className="manager-filter-label">Buscar</label>
              <Input
                icon={FiSearch}
                placeholder="Buscar por nome ou executor..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </Card>

        <Table
          items={executions}
          columns={columns}
          loading={loading}
          totalItems={totalItems}
          page={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={value => {
            setPageSize(value);
            setCurrentPage(1);
          }}
          emptyMessage={loading ? 'Carregando...' : 'Nenhuma inspeção encontrada no período.'}
        />
      </div>

      <SelectInspectionModal
        isOpen={isSelectModalOpen}
        onClose={() => setIsSelectModalOpen(false)}
        onSelect={handleTemplateSelected}
      />

      <ExecutionModal
        isOpen={isExecutionModalOpen}
        onClose={() => {
          setIsExecutionModalOpen(false);
          setExecutionToEdit(null);
          setSelectedTemplate(null);
          setSelectedTemplateCompanyId(null);
        }}
        inspection={selectedTemplate}
        executionData={executionToEdit}
        onSuccess={handleExecutionSuccess}
        companyId={selectedTemplateCompanyId}
      />

      <ViewExecutionModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        execution={executionToView}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmar exclusão"
        closeOnOverlayClick={!isDeleting}
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja excluir a execução da inspeção{' '}
            {executionToDelete && (
              <span className="delete-modal-company-name">
                {executionToDelete.inspection_name}
              </span>
            )}
            ?
          </p>
          <p>
            Essa ação não poderá ser desfeita.
          </p>
        </div>
        <div className="delete-modal-actions">
          <Button type="button" className="cancel-button" onClick={() => setIsDeleteModalOpen(false)} disabled={isDeleting}>Cancelar</Button>
          <Button type="button" onClick={confirmDelete} disabled={isDeleting}>
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        title="Compartilhar Inspeção"
        size="sm"
      >
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{ 
            width: '60px', 
            height: '60px', 
            borderRadius: '50%', 
            backgroundColor: '#e0f2fe', 
            color: '#0284c7', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            margin: '0 auto 1.5rem' 
          }}>
            <FiShare2 size={30} />
          </div>
          <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.75rem', color: '#1e293b' }}>
            Link Público Gerado
          </h3>
          <p style={{ color: '#64748b', lineHeight: '1.5', marginBottom: '1.5rem' }}>
            Qualquer pessoa com o link abaixo poderá visualizar os detalhes desta inspeção sem precisar de login.
          </p>
          
          <div style={{ 
            display: 'flex', 
            gap: '0.5rem', 
            backgroundColor: '#f8fafc', 
            padding: '0.75rem', 
            borderRadius: '8px', 
            border: '1px solid #e2e8f0',
            marginBottom: '1.5rem',
            alignItems: 'center'
          }}>
            <input 
              readOnly 
              value={shareLink} 
              style={{ 
                flex: 1, 
                border: 'none', 
                background: 'none', 
                fontSize: '0.85rem', 
                color: '#475569',
                outline: 'none'
              }} 
            />
            <button 
              onClick={copyShareLink}
              style={{ 
                background: 'var(--primary-color)', 
                color: '#fff', 
                border: 'none', 
                borderRadius: '4px', 
                padding: '4px 8px',
                cursor: 'pointer'
              }}
              title="Copiar Link"
            >
              <FiCopy size={16} />
            </button>
          </div>

          {navigator.share && (
            <Button 
              className="btn-primary" 
              style={{ width: '100%', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }} 
              onClick={handleNativeShare}
            >
              <FiExternalLink size={18} />
              Enviar para outros Apps
            </Button>
          )}

          <Button className="btn-secondary" style={{ width: '100%' }} onClick={() => setIsShareModalOpen(false)}>
            Fechar
          </Button>
        </div>
      </Modal>
    </PageComponent>
  );
}
